import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  AuthService,
  Workspace,
  WorkspaceService,
  WorkspaceWithUserRoles,
} from '@keeps-platform-frontend-workspace/kp-keeps';
import { FuseConfigService } from '@keeps-platform-frontend-workspace/layout';
import { MyAccountAPI } from 'app/shared/api/myaccount.api';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { MyAccountV2API } from '../api/myaccount-v2.api';
import { UploadWorkspaceImageDto } from '../dto';

@Injectable({ providedIn: 'root' })
export class WorkspacesService {
  private _isLoading = new BehaviorSubject(false);
  public isLoading$ = this._isLoading.asObservable();
  onWorkspacesChanged: Subject<any>;
  userWorkspacesListEvent: Subject<WorkspaceWithUserRoles[]>;
  imageUpdated: Subject<any>;

  constructor(
    private _http: MyAccountAPI,
    private _http2: MyAccountV2API,
    private _authService: AuthService,
    private _workspaceService: WorkspaceService,
    private _router: Router,
    private _fuseConfigService: FuseConfigService,
  ) {
    this.onWorkspacesChanged = new Subject();
    this.userWorkspacesListEvent = new Subject();
    this.imageUpdated = new Subject();
  }

  fetchUserAdminWorkspaces(): Observable<WorkspaceWithUserRoles[]> {
    const id = this._authService.userId;

    return this._http.get<WorkspaceWithUserRoles[]>(`/users/${id}/workspaces`).pipe(
      map((workspaces) => {
        return workspaces.filter((workspace) => {
          const roles = workspace.roles.filter((role) => role.role.key === 'company_admin');
          return roles.length;
        });
      }),
    );
  }

  public removeCurrentWorkspace(): void {
    this._workspaceService.clearCurrentWorkspace();
  }

  /**
   * Upload and update a workspace logo or an icon.
   *
   * @param uploadDto payload to update
   * @returns
   */
  uploadWorkspaceImage(uploadDto: UploadWorkspaceImageDto): Observable<Workspace> {
    const { workspaceId, file, type } = uploadDto;
    const basePath = `/workspaces/${workspaceId}`;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('width', '200');
    formData.append('height', '200');

    return this._http.postFormData<{ name: string; url: string }>(`${basePath}/${type}`, formData).pipe(
      switchMap(({ url }) => {
        const payload = type === 'icon' ? { icon_url: url } : { logo_url: url };
        return this._http.patch<Workspace>(basePath, payload);
      }),
      tap((workspace) => this._workspaceService.setCurrentWorkspace(workspace)),
    );
  }

  createWorkspace(workspace: Workspace) {
    return this._http2.post('/workspaces', workspace).pipe(
      tap((workspace) => {
        this._workspaceService.setCurrentWorkspace(workspace);
        this._router.navigate(['workspace', 'profile']);
      }),
    );
  }

  updateWorkspace(workspace: Workspace) {
    const {
      theme_dark,
      custom_color,
      name,
      doc_number,
      duns_number,
      description,
      address,
      city,
      state,
      post_code,
      country,
      id,
    } = workspace;

    const payload = {
      theme_dark,
      custom_color,
      name,
      doc_number,
      duns_number,
      description,
      address,
      city,
      state,
      post_code,
      country,
    };

    return this._http2
      .patch(`/workspaces/${id}`, payload)
      .pipe(tap((workspace) => this._workspaceService.setCurrentWorkspace(workspace)));
  }

  updateWorkspaceCustomColor(custom_color: string, workspace: Workspace) {
    const payload = { ...workspace, custom_color };
    return this.updateWorkspace(payload).pipe(
      tap(() => (this._fuseConfigService.config = { themeColor: custom_color })),
    );
  }

  updateWorkspaceDarkTheme(theme_dark: boolean, workspace: Workspace) {
    const payload = { ...workspace, theme_dark };
    return this.updateWorkspace(payload).pipe(
      tap(() => (this._fuseConfigService.config = { scheme: theme_dark ? 'dark' : 'light' })),
    );
  }

  deleteWorkspace(id: string) {
    return this._http2.delete(`/workspaces/${id}`).pipe(
      tap(() => {
        this.removeCurrentWorkspace();
        this._router.navigate(['/', 'workspaces']);
      }),
    );
  }
}
